import { useMemo, useState } from "react";
import { useStore, uid } from "../store/store";
import { useT } from "../i18n/useT";
import {
  evaluateSend,
  handleInbound,
  type ContactSnapshot,
  type CampaignSnapshot,
} from "@mensajeando/compliance";
import { step, type ConversationState } from "@mensajeando/flows";

export function ManualSend() {
  const t = useT();
  const campaigns = useStore((s) => s.campaigns);
  const contacts = useStore((s) => s.contacts);
  const consents = useStore((s) => s.consents);
  const flows = useStore((s) => s.flows);
  const orgs = useStore((s) => s.organizations);
  const clients = useStore((s) => s.clients);

  const appendEvent = useStore((s) => s.appendEvent);
  const updateContact = useStore((s) => s.updateContact);
  const addConsent = useStore((s) => s.addConsent);
  const updateCampaign = useStore((s) => s.updateCampaign);

  const [campaignId, setCampaignId] = useState(campaigns[0]?.id ?? "");
  const [contactId, setContactId] = useState(contacts[0]?.id ?? "");
  const [inbound, setInbound] = useState("");
  const [log, setLog] = useState<string[]>([]);

  const campaign = campaigns.find((c) => c.id === campaignId);
  const contact = contacts.find((c) => c.id === contactId);
  const org = orgs.find((o) => o.id === campaign?.organizationId);
  const client = clients.find((c) => c.id === campaign?.clientId);

  const policy = useMemo(() => {
    if (!campaign || !contact) return undefined;
    const contactConsents = consents.filter((x) => x.contactId === contact.id);
    const contactSnap: ContactSnapshot = {
      id: contact.id,
      phoneE164: contact.phoneE164,
      language: contact.language,
      timezone: contact.timezone,
      isLandline: contact.isLandline,
      isReassignedRisk: contact.isReassignedRisk,
      consentBySource: Object.fromEntries(
        contactConsents.map((c) => [c.id, {
          channel: c.channel, status: c.status, at: c.at, disclosureVersion: c.disclosureVersion,
        }]),
      ),
      suppressed: contact.suppressed,
      suppressionReason: contact.suppressionReason,
    };
    const campSnap: CampaignSnapshot = {
      id: campaign.id,
      channel: campaign.channel,
      senderEligible: campaign.senderEligible,
      campaignRegistered: campaign.campaignRegistered,
      requiresConsent: campaign.requiresConsent,
      organizationId: campaign.organizationId,
    };
    return { result: evaluateSend({ contact: contactSnap, campaign: campSnap }), contactSnap, campSnap };
  }, [campaign, contact, consents]);

  function logLine(s: string) {
    setLog((l) => [...l, `${new Date().toLocaleTimeString()} · ${s}`]);
  }

  function pressSend() {
    if (!campaign || !contact || !policy) return;
    if (!policy.result.ok) {
      logLine(`✖ Blocked: ${policy.result.code} — ${policy.result.reason}`);
      return;
    }
    const flow = flows[campaign.flowId];
    if (!flow) return;

    // Begin or continue conversation
    let state: ConversationState = {
      flowId: flow.id,
      contactId: contact.id,
      language: campaign.language === "auto" ? contact.language : campaign.language,
      currentNode: flow.entry,
      answers: {},
      tags: new Set(contact.tags),
    };
    const out = step(flow, state);
    state = out.state;

    if (out.outboundBody) {
      appendEvent({
        id: uid("ev"),
        campaignId: campaign.id,
        contactId: contact.id,
        channel: campaign.channel,
        language: state.language,
        direction: "outbound",
        status: "delivered", // simulated; real impl would use delivery receipts
        body: out.outboundBody,
        flowNodeId: state.currentNode,
        at: new Date().toISOString(),
      });
      logLine(`→ Sent (${state.language}): ${out.outboundBody}`);
    }
    updateCampaign(campaign.id, { startedConversations: campaign.startedConversations + 1 });
  }

  function simulateInbound() {
    if (!campaign || !contact || !inbound.trim() || !org || !client) return;
    // Always pass through compliance opt-out handler first
    const handled = handleInbound({
      body: inbound,
      channel: campaign.channel,
      fromE164: contact.phoneE164,
      toE164: "+15555550100",
      receivedAt: new Date().toISOString(),
      campaignId: campaign.id,
      organizationId: campaign.organizationId,
      contactLanguage: contact.language,
      campaignType: campaign.type,
      clientName: client.name,
      supportEmail: org.supportEmail,
    });

    appendEvent({
      id: uid("ev"),
      campaignId: campaign.id,
      contactId: contact.id,
      channel: campaign.channel,
      language: contact.language,
      direction: "inbound",
      body: inbound,
      at: new Date().toISOString(),
      optOut: handled.action === "OPT_OUT",
    });
    logLine(`← Inbound: "${inbound}" → ${handled.action} (matched: ${handled.match.matched || "—"})`);

    if (handled.action === "OPT_OUT" && handled.suppression) {
      updateContact(contact.id, {
        suppressed: true,
        suppressionReason: handled.suppression.reason,
      });
      addConsent({
        id: uid("consent"),
        contactId: contact.id,
        channel: campaign.channel,
        language: contact.language,
        status: "revoked",
        source: "inbound_keyword",
        disclosureVersion: "2026-01-15.v1",
        at: handled.auditEvent.at,
      });
      if (handled.replyText) {
        appendEvent({
          id: uid("ev"),
          campaignId: campaign.id,
          contactId: contact.id,
          channel: campaign.channel,
          language: contact.language,
          direction: "outbound",
          status: "delivered",
          body: handled.replyText,
          at: new Date().toISOString(),
        });
        logLine(`→ STOP confirmation sent: ${handled.replyText}`);
      }
    } else if (handled.action === "HELP_REPLY" && handled.replyText) {
      appendEvent({
        id: uid("ev"),
        campaignId: campaign.id,
        contactId: contact.id,
        channel: campaign.channel,
        language: contact.language,
        direction: "outbound",
        status: "delivered",
        body: handled.replyText,
        at: new Date().toISOString(),
      });
      logLine(`→ HELP reply: ${handled.replyText}`);
    } else if (handled.action === "OPT_IN_RESUME" && handled.replyText) {
      updateContact(contact.id, { suppressed: false, suppressionReason: undefined });
      logLine(`→ Resume opt-in: ${handled.replyText}`);
    }

    setInbound("");
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">{t.send.title}</h1>
        <p className="text-sm text-slate-500">{t.send.help}</p>
      </header>

      <div className="grid grid-cols-2 gap-4">
        <div className="card card-pad space-y-3">
          <div>
            <label className="label">{t.send.pickCampaign}</label>
            <select className="input" value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">{t.send.pickContact}</label>
            <select className="input" value={contactId} onChange={(e) => setContactId(e.target.value)}>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.firstName} · {c.phoneE164} · {c.language.toUpperCase()}</option>)}
            </select>
          </div>

          <div className="rounded-lg border border-slate-200 p-3">
            <div className="text-xs uppercase text-slate-500 mb-2">{t.send.checks}</div>
            {policy?.result.ok
              ? <div className="badge-ok">{t.send.pass} — ready to send</div>
              : <div className="badge-err">{t.send.block} — {policy?.result.code}: {policy?.result.reason}</div>}
          </div>

          <button className="btn-primary w-full" disabled={!policy?.result.ok} onClick={pressSend}>
            ▶ {t.send.reviewSend}
          </button>

          <div className="border-t pt-3">
            <label className="label">Simulate inbound reply</label>
            <div className="flex gap-2">
              <input className="input flex-1" placeholder="STOP, HELP, 1, SI, ayuda…" value={inbound} onChange={(e) => setInbound(e.target.value)} />
              <button className="btn-secondary" onClick={simulateInbound}>Receive</button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">STOP/HELP keywords run through the compliance handler and auto-suppress where applicable.</p>
          </div>
        </div>

        <div className="card card-pad">
          <h2 className="font-semibold mb-2">Activity log</h2>
          <div className="rounded bg-slate-50 p-3 text-xs font-mono h-80 overflow-auto">
            {log.length === 0
              ? <span className="text-slate-400">No activity yet.</span>
              : log.map((l, i) => <div key={i}>{l}</div>)}
          </div>
        </div>
      </div>
    </div>
  );
}
